import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import { colors } from '../constants';
import { fmt, fmtN, pct } from './format';

/**
 * The Insights content model.
 *
 * Four topics, five questions each, every answer expressed as a list of
 * presentation blocks (see `components/InsightBlocks.jsx`). The rules are
 * entirely client-side: they read the dashboard the engine already computed and
 * phrase it, so nothing here re-derives profit.
 *
 * A question whose data is missing answers with prose instead of blocks, which
 * is why `AT` exists alongside `A` — a chart of zeroes would read as a finding
 * rather than an absence.
 */

function A(blocks, followups = []) {
  return { blocks, followups };
}

function AT(text, followups = []) {
  return { text, blocks: [], followups };
}

export const IMPORT_HINT =
  "There isn't enough sales data in this period to answer this yet. Import more sales (Settings → Import) or pick a wider date range to unlock this insight.";

export function buildInsightTopics(data) {
  const totalRevenue = data.totalRevenue || 0;
  const foodCostPct = Number.isFinite(data.foodCostPct) ? data.foodCostPct : 0;

  // Picked by the engine over every item that sold. None of them could be found
  // in this response otherwise: it carries one page of the list, and the weakest
  // performer is at the far end of it by definition.
  const highlights = data.itemHighlights ?? {};
  const top = highlights.topRevenue ?? {
    name: 'your top item',
    price: 0,
    salesPrice: 0,
    unitsSold: 0,
    foodCostPct: 32,
    category: '',
    fcd: 0,
    grossProfit: 0,
    grossMarginPct: 0,
  };
  const topRev = top.salesPrice * top.unitsSold;
  const topShare = totalRevenue > 0 ? (topRev / totalRevenue) * 100 : 0;
  // Taken from the engine rather than recomputed: cost is a share of the menu
  // price while profit is measured against what the line sold for, so the
  // `price * (1 - foodCostPct)` shortcut no longer holds.
  const topGP = top.grossProfit;
  const topGPtotal = topGP * top.unitsSold;

  const pos =
    data.channels.find((c) => c.channelId === 'pos') ??
    data.channels[0] ?? { revenue: 0, name: 'POS' };
  const direct =
    data.channels.find((c) =>
      /direct|website|food2/i.test(c.channelId + c.name)
    ) ?? { revenue: 0 };
  const commFree = pos.revenue + direct.revenue;
  const commFreePct = totalRevenue > 0 ? (commFree / totalRevenue) * 100 : 0;

  const deliveryChs = data.channels.filter((c) => c.commission > 0);
  const deliveryRev = deliveryChs.reduce((s, c) => s + c.revenue, 0);
  const deliveryFees = deliveryChs.reduce(
    (s, c) => s + (c.revenue * c.commission) / 100,
    0
  );
  const worstCh = [...deliveryChs].sort(
    (a, b) => b.revenue * b.commission - a.revenue * a.commission
  )[0];
  const effDeliveryRate =
    deliveryRev > 0 ? (deliveryFees / deliveryRev) * 100 : 0;

  const bestMargin = highlights.bestMargin ?? top;
  const highVol = highlights.highestVolume ?? top;
  const lowPerf = highlights.lowestRevenue ?? top;

  const itemsSold = data.itemsSold ?? 0;
  const hasSales = totalRevenue > 0 && itemsSold > 0 && topRev > 0;
  const hasMultipleItems = itemsSold >= 2;
  const hasDelivery = deliveryChs.length > 0 && deliveryRev > 0;
  const hasChannels = data.channels.some((c) => c.revenue > 0);

  return {
    topseller: {
      key: 'topseller',
      icon: EmojiEventsOutlinedIcon,
      tag: 'Top seller',
      color: '#22c55e',
      title: hasSales ? `${top.name} is your engine` : 'Find your top seller',
      summary: hasSales
        ? `${fmtN(top.unitsSold)} sold · ${fmt(topRev)} (${pct(topShare)} of revenue)`
        : 'Import sales data to see which item drives your revenue',
      qs: [
        {
          q: hasSales
            ? `How much profit does ${top.name} actually make?`
            : 'How much profit does my top item make?',
          a: !hasSales
            ? AT(IMPORT_HINT)
            : A(
                [
                  {
                    kind: 'stats',
                    cards: [
                      { label: 'Price', value: fmt(top.salesPrice) },
                      { label: 'Food cost', value: pct(top.foodCostPct) },
                      {
                        label: 'Profit / unit',
                        value: fmt(topGP),
                        tone: 'good',
                        highlight: true,
                      },
                    ],
                  },
                  {
                    kind: 'bars',
                    title: `Gross profit · ${fmtN(top.unitsSold)} sold`,
                    rows: [
                      {
                        label: 'Revenue',
                        value: fmt(topRev),
                        pct: 100,
                        tone: 'neutral',
                      },
                      {
                        // Cost is a share of the menu price, so it is taken per
                        // unit from the engine; the shares are of what the item
                        // sold for, which keeps the three rows adding up.
                        label: 'Food cost',
                        value: fmt(top.fcd * top.unitsSold),
                        pct: 100 - top.grossMarginPct,
                        tone: 'bad',
                      },
                      {
                        label: 'You keep',
                        value: fmt(topGPtotal),
                        pct: top.grossMarginPct,
                        tone: 'good',
                      },
                    ],
                  },
                  {
                    kind: 'takeaway',
                    text: 'Your #1 profit lever — small price moves here matter most. This is before labor and overhead.',
                  },
                ],
                [
                  { label: 'Should I raise its price?', target: 1 },
                  { label: 'What if I bundle it?', target: 2 },
                ]
              ),
        },
        {
          q: 'Should I raise the price on delivery apps?',
          a: !hasSales
            ? AT(IMPORT_HINT)
            : A(
                [
                  {
                    kind: 'compare',
                    title: 'Same item, what you keep',
                    items: [
                      {
                        title: 'POS / Direct',
                        value: fmt(top.salesPrice),
                        sub: 'keep ~100%',
                        tone: 'good',
                      },
                      {
                        title: 'Uber / DoorDash',
                        value: fmt(top.salesPrice * 0.7),
                        sub: 'after 30% fee',
                        tone: 'bad',
                      },
                    ],
                  },
                  {
                    kind: 'stats',
                    cards: [
                      { label: 'Current app price', value: fmt(top.price) },
                      {
                        label: 'Suggested (+15%)',
                        value: fmt(top.price * 1.15),
                        tone: 'good',
                        highlight: true,
                      },
                      { label: 'Recovers', value: fmt(top.price * 0.15) },
                    ],
                  },
                  {
                    kind: 'takeaway',
                    text: 'Raise the delivery menu price ~15%. Keep your POS/direct price as-is — customers rarely notice on a top seller.',
                  },
                ],
                [
                  { label: 'How dependent am I on this item?', target: 4 },
                  { label: 'Compare to similar restaurants', target: 3 },
                ]
              ),
        },
        {
          q: 'What if I bundle it with a drink or side?',
          a: !hasSales
            ? AT(IMPORT_HINT)
            : A(
                [
                  {
                    kind: 'stats',
                    cards: [
                      { label: 'Drink price', value: fmt(2.5) },
                      { label: 'Drink cost', value: '≈18%' },
                      {
                        label: 'Extra profit',
                        value: fmt(Math.round(top.unitsSold / 3) * 2.5 * 0.82),
                        tone: 'good',
                        highlight: true,
                      },
                    ],
                  },
                  {
                    kind: 'takeaway',
                    text: `Attach a drink to even a third of ${top.name}'s ${fmtN(
                      top.unitsSold
                    )} orders — pure margin with zero extra labor, and it makes delivery pricing less obvious.`,
                  },
                ],
                [
                  {
                    label: 'What else should I upsell?',
                    target: null,
                    goto: 'hiddengem',
                  },
                  { label: 'How much profit does it make?', target: 0 },
                ]
              ),
        },
        {
          q: hasSales
            ? `Is ${top.name} priced right vs similar restaurants?`
            : 'Is my top item priced right vs similar restaurants?',
          a: !hasSales
            ? AT(IMPORT_HINT)
            : A(
                [
                  {
                    kind: 'compare',
                    title: 'Your price vs market',
                    items: [
                      {
                        title: 'You',
                        value: fmt(top.price),
                        sub: 'current',
                        tone: 'accent',
                      },
                      {
                        title: 'Similar spots',
                        value: '±$2',
                        sub: 'typical range',
                        tone: 'neutral',
                      },
                    ],
                  },
                  {
                    kind: 'takeaway',
                    text: "You're in range. The real leak is your delivery-app price, not your base price.",
                  },
                ],
                [{ label: 'Should I raise delivery price?', target: 1 }]
              ),
        },
        {
          q: 'How dependent am I on this one item?',
          a: !hasSales
            ? AT(IMPORT_HINT)
            : A(
                [
                  {
                    kind: 'stats',
                    cards: [
                      {
                        label: 'Share of revenue',
                        value: pct(topShare),
                        tone: 'accent',
                        highlight: true,
                      },
                      { label: 'Revenue', value: fmt(topRev) },
                      {
                        label: `At risk ${pct(-20)}`,
                        value: fmt(topRev * 0.2),
                        tone: 'bad',
                      },
                    ],
                  },
                  {
                    kind: 'bars',
                    title: 'Where revenue comes from',
                    rows: [
                      {
                        label: top.name,
                        value: pct(topShare),
                        pct: topShare,
                        tone: 'accent',
                      },
                      {
                        label: 'Everything else',
                        value: pct(100 - topShare),
                        pct: 100 - topShare,
                        tone: 'neutral',
                      },
                    ],
                  },
                  {
                    kind: 'takeaway',
                    text: 'A hero item is a strength and a risk. Protect its quality and consistency — and nurture a #2 item.',
                  },
                ],
                [
                  {
                    label: "What's my hidden gem?",
                    target: null,
                    goto: 'hiddengem',
                  },
                ]
              ),
        },
      ],
    },

    channelwin: {
      key: 'channelwin',
      icon: AttachMoneyIcon,
      tag: 'Channel win',
      color: colors.primary,
      title: hasSales
        ? `${pct(commFreePct)} of revenue is commission-free`
        : 'Track your commission-free revenue',
      summary: hasSales
        ? `POS + Direct = ${fmt(commFree)} with no commission`
        : 'Import sales data to see how much you keep fee-free',
      qs: [
        {
          q: 'How much am I saving by being commission-free?',
          a: !hasSales
            ? AT(IMPORT_HINT)
            : A(
                [
                  {
                    kind: 'stats',
                    cards: [
                      { label: 'Fee-free revenue', value: fmt(commFree) },
                      {
                        label: 'Fees avoided',
                        value: fmt(commFree * 0.25),
                        tone: 'good',
                        highlight: true,
                      },
                      {
                        label: 'Share',
                        value: pct(commFreePct),
                        tone: 'accent',
                      },
                    ],
                  },
                  {
                    kind: 'takeaway',
                    text: 'If this revenue ran through delivery apps at ~25%, those fees would be gone. Most restaurants your size are far more app-dependent.',
                  },
                ],
                [
                  { label: 'How do I push more direct?', target: 2 },
                  { label: "What's my take-home per channel?", target: 3 },
                ]
              ),
        },
        {
          q: 'What would I lose if delivery apps grew?',
          a: A(
            [
              {
                kind: 'bars',
                title: `Every ${fmt(1000)} that shifts to a 30% app`,
                rows: [
                  { label: 'You keep', value: fmt(700), pct: 70, tone: 'good' },
                  { label: 'App takes', value: fmt(300), pct: 30, tone: 'bad' },
                ],
              },
              {
                kind: 'takeaway',
                text: "Protecting your direct share is one of the highest-ROI moves you can make — it's pure margin defense.",
              },
            ],
            [{ label: 'How do I push more direct?', target: 2 }]
          ),
        },
        {
          q: 'How do I push more orders to direct/POS?',
          a: A(
            [
              {
                kind: 'tips',
                items: [
                  'Put an "order direct & save" insert in every delivery bag.',
                  'Price direct slightly below the apps so regulars switch.',
                  'Offer a small loyalty perk only on direct orders.',
                ],
              },
              {
                kind: 'takeaway',
                text: 'Even a 10-point shift from apps to direct on your volume is meaningful margin.',
              },
            ],
            [{ label: 'How much am I saving today?', target: 0 }]
          ),
        },
        {
          q: "What's my real take-home per channel?",
          a: !hasChannels
            ? AT(
                'No channel revenue in this period yet — import sales from your POS or delivery apps to compare what each channel keeps.'
              )
            : A(
                [
                  {
                    kind: 'bars',
                    title: 'What each channel keeps after commission',
                    rows: (() => {
                      const kept = data.channels.map((c) => ({
                        name: c.name,
                        commission: c.commission,
                        keep: c.revenue * (1 - c.commission / 100),
                      }));
                      const max = Math.max(1, ...kept.map((k) => k.keep));
                      return kept.map((k) => ({
                        label:
                          k.commission > 0
                            ? // pct() brings its own parentheses for a
                              // deduction: "Uber Eats (30.00%)".
                              `${k.name} ${pct(-k.commission)}`
                            : k.name,
                        value: fmt(k.keep),
                        pct: (k.keep / max) * 100,
                        tone: k.commission > 0 ? 'warn' : 'good',
                      }));
                    })(),
                  },
                  {
                    kind: 'takeaway',
                    text: 'POS and direct keep ~100% (minus card fees); the apps keep 70–75%. Same food, very different take-home.',
                  },
                ],
                [
                  {
                    label: 'Should I add a delivery surcharge?',
                    target: null,
                    goto: 'delivery',
                  },
                ]
              ),
        },
        {
          q: 'Is my direct channel growing or shrinking?',
          a: !hasSales
            ? AT(IMPORT_HINT)
            : A(
                [
                  {
                    kind: 'stats',
                    cards: [
                      { label: 'Direct revenue', value: fmt(direct.revenue) },
                      {
                        label: 'Share of revenue',
                        value: pct(
                          totalRevenue > 0
                            ? (direct.revenue / totalRevenue) * 100
                            : 0
                        ),
                        tone: 'accent',
                        highlight: true,
                      },
                    ],
                  },
                  {
                    kind: 'takeaway',
                    text: 'Upload each month in Settings → Costs to track the trend — direct share climbing is exactly what you want to see.',
                  },
                ],
                [{ label: 'How do I push more direct?', target: 2 }]
              ),
        },
      ],
    },

    delivery: {
      key: 'delivery',
      icon: WarningAmberIcon,
      tag: 'Delivery cost',
      color: '#F97316',
      title: hasDelivery
        ? `Delivery is costing you ${fmt(deliveryFees)} in fees`
        : 'No delivery-app fees this period',
      summary: hasDelivery
        ? `${fmt(deliveryRev)} delivery revenue · ${fmt(deliveryFees)} in commission`
        : 'No commission channels detected — you keep 100% of every order',
      qs: [
        {
          q: 'How much am I paying in delivery commissions?',
          a: !hasDelivery
            ? AT(
                "No delivery-app revenue in this period — so you're paying $0 in commissions. If you do sell on apps like Uber Eats or DoorDash, import those sales to see what they cost you.",
                [{ label: 'Should I add a delivery surcharge?', target: 2 }]
              )
            : A(
                [
                  {
                    kind: 'stats',
                    cards: [
                      { label: 'Delivery revenue', value: fmt(deliveryRev) },
                      {
                        label: 'Lost to fees',
                        value: fmt(deliveryFees),
                        tone: 'bad',
                        highlight: true,
                      },
                      {
                        label: 'Effective rate',
                        value: pct(effDeliveryRate),
                      },
                    ],
                  },
                  {
                    kind: 'takeaway',
                    text: `That's off the top — before any food or labor cost — across ${
                      deliveryChs.map((c) => c.name).join(', ') ||
                      'your delivery apps'
                    }.`,
                  },
                ],
                [
                  { label: 'Which app costs me the most?', target: 1 },
                  { label: 'Is delivery even profitable?', target: 4 },
                ]
              ),
        },
        {
          q: 'Which app costs me the most?',
          a:
            hasDelivery && worstCh
              ? A(
                  [
                    {
                      kind: 'bars',
                      title: 'Commission paid per app',
                      rows: (() => {
                        const fees = [...deliveryChs]
                          .map((c) => ({
                            name: c.name,
                            commission: c.commission,
                            fee: (c.revenue * c.commission) / 100,
                          }))
                          .sort((a, b) => b.fee - a.fee);
                        const max = Math.max(1, ...fees.map((f) => f.fee));
                        return fees.map((f) => ({
                          label: `${f.name} (${pct(f.commission)})`,
                          value: fmt(f.fee),
                          pct: (f.fee / max) * 100,
                          tone: 'warn',
                        }));
                      })(),
                    },
                    {
                      kind: 'takeaway',
                      text: `${worstCh.name} is your priciest channel — focus markups or an "order direct" push there.`,
                    },
                  ],
                  [{ label: 'What markup offsets it?', target: 3 }]
                )
              : AT(
                  'No delivery channels in this period — nothing is taking a cut of your orders. If you sell on delivery apps, import those sales to compare their costs.',
                  [{ label: 'What markup offsets it?', target: 3 }]
                ),
        },
        {
          q: 'Should I add a delivery surcharge?',
          a: A(
            [
              {
                kind: 'verdict',
                title: 'Surcharge vs delivery menu',
                items: [
                  { label: 'Flat surcharge', ok: false, sub: 'feels punitive' },
                  {
                    label: 'Higher app price',
                    ok: true,
                    sub: 'invisible markup',
                  },
                ],
              },
              {
                kind: 'takeaway',
                text: 'Bake a 15–20% markup into app prices instead of a visible fee — the commission gets covered invisibly.',
              },
            ],
            [{ label: 'What markup offsets it exactly?', target: 3 }]
          ),
        },
        {
          q: 'What price markup offsets the commission?',
          a: A(
            [
              {
                kind: 'bars',
                title: 'Markup needed',
                rows: [
                  {
                    label: 'Full offset (30%)',
                    value: '43% — too high',
                    pct: 100,
                    tone: 'bad',
                  },
                  {
                    label: 'Recommended',
                    value: '15–20%',
                    pct: 42,
                    tone: 'good',
                  },
                ],
              },
              {
                kind: 'takeaway',
                text: '15–20% recovers most of the fee while staying competitive. Apply it to your top sellers first.',
              },
            ],
            [
              {
                label: 'How dependent am I on my top seller?',
                target: null,
                goto: 'topseller',
              },
            ]
          ),
        },
        {
          q: 'Is delivery even profitable for me?',
          a: !hasDelivery
            ? AT(
                "You have no delivery-app revenue in this period, so there's nothing eating into your margin here. Import delivery sales to see whether the apps are worth it for you.",
                [{ label: 'What markup should I add?', target: 3 }]
              )
            : A(
                [
                  {
                    kind: 'bars',
                    title: 'Where a delivery dollar goes',
                    rows: [
                      {
                        label: 'Commission',
                        value: pct(effDeliveryRate),
                        pct: effDeliveryRate,
                        tone: 'bad',
                      },
                      {
                        label: 'Food cost',
                        value: `~${pct(foodCostPct)}`,
                        pct: foodCostPct,
                        tone: 'warn',
                      },
                      {
                        label: 'Left for you',
                        value: pct(
                          Math.max(0, 100 - effDeliveryRate - foodCostPct)
                        ),
                        pct: Math.max(0, 100 - effDeliveryRate - foodCostPct),
                        tone: 'good',
                      },
                    ],
                  },
                  {
                    kind: 'takeaway',
                    text: 'Delivery is worth it for volume and reach — but only if your app prices carry a markup. Without one, you may be subsidizing the apps.',
                  },
                ],
                [{ label: 'What markup should I add?', target: 3 }]
              ),
        },
      ],
    },

    hiddengem: {
      key: 'hiddengem',
      icon: StarBorderIcon,
      tag: 'Hidden gem',
      color: '#8B5CF6',
      title: hasSales
        ? `${bestMargin.name} is a margin hero`
        : 'Find your hidden gem',
      summary: hasSales
        ? `${pct(bestMargin.foodCostPct)} food cost · ${fmtN(
            highVol.unitsSold
          )} of ${highVol.name} sold`
        : 'Import sales data to spot your best-margin items',
      qs: [
        {
          q: "What's my highest-margin item?",
          a: !hasSales
            ? AT(IMPORT_HINT)
            : A(
                [
                  {
                    kind: 'stats',
                    cards: [
                      { label: 'Price', value: fmt(bestMargin.salesPrice) },
                      {
                        label: 'Food cost',
                        value: pct(bestMargin.foodCostPct),
                      },
                      {
                        label: 'You keep',
                        value: fmt(bestMargin.grossProfit),
                        tone: 'good',
                        highlight: true,
                      },
                    ],
                  },
                  {
                    kind: 'bars',
                    // Shares of what the item sells for, so they match the
                    // dollars above: food cost is the remainder of the margin,
                    // not `foodCostPct`, which is a share of the menu price.
                    title: `Every ${fmt(bestMargin.salesPrice)} of ${bestMargin.name}`,
                    rows: [
                      {
                        label: 'Food cost',
                        value: pct(100 - bestMargin.grossMarginPct),
                        pct: 100 - bestMargin.grossMarginPct,
                        tone: 'bad',
                      },
                      {
                        label: 'You keep',
                        value: pct(bestMargin.grossMarginPct),
                        pct: bestMargin.grossMarginPct,
                        tone: 'good',
                      },
                    ],
                  },
                  {
                    kind: 'takeaway',
                    text: 'Low-cost, high-margin items like this are exactly what you want to push harder.',
                  },
                ],
                [
                  { label: 'Which items should I upsell?', target: 1 },
                  { label: 'Where am I leaving money?', target: 3 },
                ]
              ),
        },
        {
          q: 'Which items should I upsell?',
          a: !hasSales
            ? AT(IMPORT_HINT)
            : A(
                [
                  {
                    kind: 'stats',
                    cards: [
                      {
                        label: 'Top volume',
                        value: `${fmtN(highVol.unitsSold)} sold`,
                        tone: 'accent',
                        highlight: true,
                      },
                      { label: 'Item', value: highVol.name },
                    ],
                  },
                  {
                    kind: 'takeaway',
                    text: `${highVol.name} is your highest-volume item and cheap to make. An "add a drink/side?" prompt at checkout on direct orders is free money with no extra labor.`,
                  },
                ],
                [{ label: 'What add-ons boost my ticket?', target: 4 }]
              ),
        },
        {
          q: 'What low performers should I cut?',
          a: !hasSales
            ? AT(IMPORT_HINT)
            : !hasMultipleItems
              ? AT(
                  "Only one item has sales in this period, so there's no low performer to compare yet. Import more sales or widen the date range to rank your menu.",
                  [{ label: "What's my highest-margin item?", target: 0 }]
                )
              : A(
                  [
                    {
                      kind: 'stats',
                      cards: [
                        { label: 'Lowest earner', value: lowPerf.name },
                        {
                          label: 'Units sold',
                          value: fmtN(lowPerf.unitsSold),
                        },
                        {
                          label: 'Revenue',
                          value: fmt(lowPerf.salesPrice * lowPerf.unitsSold),
                          tone: 'bad',
                          highlight: true,
                        },
                      ],
                    },
                    {
                      kind: 'takeaway',
                      text: 'Slow items still take up prep space, inventory, and menu attention. Consider cutting or reworking the bottom few to focus on what sells.',
                    },
                  ],
                  [{ label: "What's my highest-margin item?", target: 0 }]
                ),
        },
        {
          q: 'Where am I leaving money on the table?',
          a: A(
            [
              {
                kind: 'tips',
                items: [
                  "No markup on delivery apps — you're paying full commission.",
                  'Under-upselling high-margin add-ons like drinks and sides.',
                ],
              },
              {
                kind: 'takeaway',
                text: 'Fixing either is faster than raising base prices — and customers barely notice.',
              },
            ],
            [
              {
                label: 'How do app fees hurt me?',
                target: null,
                goto: 'delivery',
              },
              { label: 'Which items to upsell?', target: 1 },
            ]
          ),
        },
        {
          q: 'What add-ons boost my average ticket?',
          a: A(
            [
              {
                kind: 'bars',
                title: 'Classic ticket-boosters (food cost — lower is better)',
                rows: [
                  { label: 'Dips', value: '~15%', pct: 15, tone: 'good' },
                  { label: 'Drinks', value: '~18%', pct: 18, tone: 'good' },
                  {
                    label: 'Sides / fries',
                    value: '~20%',
                    pct: 20,
                    tone: 'good',
                  },
                ],
              },
              {
                kind: 'takeaway',
                text: `Attach a ${fmt(
                  2.5
                )} drink to even 30% of orders — pure margin with zero kitchen complexity. Make the upsell one click on your direct site.`,
              },
            ],
            [{ label: "What's my highest-margin item?", target: 0 }]
          ),
        },
      ],
    },
  };
}
